export class SearchUtils {
  // --- helpers ---
  static normalize(s: string) {
    return (
      s
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim() ?? ""
    );
  }

  static tokenize(q: string): { term: string; neg: boolean; raw: string }[] {
    const out: { term: string; neg: boolean; raw: string }[] = [];
    const re = /"([^"]+)"|(\S+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(q))) {
      const raw = m[1] ?? m[2];
      if (!raw) continue;
      const neg = raw.startsWith("-");
      const term = this.normalize(neg ? raw.slice(1) : raw);
      if (term) out.push({ term, neg, raw });
    }
    return out;
  }

  static isWordStart(s: string, i: number) {
    return i === 0 || /\W/.test(s[i - 1] ?? " ");
  }

  static isWholeWord(s: string, i: number, len: number) {
    return (
      this.isWordStart(s, i) &&
      (i + len >= s.length || /\W/.test(s[i + len] ?? " "))
    );
  }

  // --- fuzzy léger (tolère 1 faute pour tokens >= 4) ---
  static hasFuzzy(ref: string, term: string): { ok: boolean; pos: number } {
    if (term.length < 4) return { ok: false, pos: -1 };
    const w = term.length;
    let best = -1;
    for (let i = 0; i + w <= ref.length; i++) {
      const seg = ref.slice(i, i + w);
      let diff = 0;
      for (let k = 0; k < w; k++) if (seg[k] !== term[k]) diff++;
      if (diff <= 1) {
        best = i;
        break;
      }
    }
    return { ok: best >= 0, pos: best };
  }

  // --- détection de la lettre variante près de FAB (dans les 2 sens) ---
  private static word(s: string) {
    return `\\b${s}\\b`;
  }

  private static VARIANT_NEAR_FAB_FWD = new RegExp(
    `${SearchUtils.word("fab")}\\W{0,3}\\b([a-z])\\b`,
  );
  private static VARIANT_NEAR_FAB_BACK = new RegExp(
    `\\b([a-z])\\b\\W{0,3}${SearchUtils.word("fab")}`,
  );

  private static getRefFabVariant(refN: string): string | null {
    const fwd = this.VARIANT_NEAR_FAB_FWD.exec(refN)?.[1] ?? null; // "FAB B"
    const back = this.VARIANT_NEAR_FAB_BACK.exec(refN)?.[1] ?? null; // "B FAB"
    return fwd ?? back;
  }

  static isIncluded(query: string, ref: string) {
    return this.score(query, ref) > 0;
  }

  static isIncludedScore(query: string, ref: string) {
    return this.score(query, ref);
  }

  // --- score principal ---
  static score(query: string, ref: string): number {
    if (!query || !ref) return 0;

    const refN = this.normalize(ref);
    const toks = this.tokenize(query);
    const must = toks.filter((t) => !t.neg);
    const nots = toks.filter((t) => t.neg);

    // Exclusions
    if (nots.some((t) => refN.includes(t.term))) return 0;

    // Heuristique: ignorer tokens d’1 lettre… sauf si “FAB ± lettre” du ref correspond à la lettre saisie
    const singles = must.filter((t) => t.term.length === 1);
    const multi = must.filter((t) => t.term.length > 1);

    const hasFab = /\bfab\b/.test(refN);
    const refVar = this.getRefFabVariant(refN); // lettre présente près de FAB dans la ref
    const qVariant = singles.find((s) => s.term.length === 1)?.term ?? null; // première lettre seule tapée

    // on ancre la/les lettres simples seulement si elles matchent la lettre de variante détectée
    const anchoredSingles = singles.filter((s) => refVar && s.term === refVar);

    const effectiveTokens = [...multi, ...anchoredSingles];

    // Tous les tokens “effectifs” doivent matcher (includes ou fuzzy)
    const positions: number[] = [];
    for (const t of effectiveTokens) {
      let idx = refN.indexOf(t.term);
      if (idx === -1) {
        const fz = this.hasFuzzy(refN, t.term);
        if (!fz.ok) return 0;
        idx = fz.pos;
      }
      positions.push(idx);
    }

    // Score par token
    let score = 0;
    for (let i = 0; i < effectiveTokens.length; i++) {
      const t = effectiveTokens[i].term;
      const idx = positions[i];
      let s = 1.0;
      if (idx === 0) s += 0.6;
      if (this.isWordStart(refN, idx)) s += 0.4;
      if (this.isWholeWord(refN, idx, t.length)) s += 0.3;
      s += Math.min(t.length / 12, 0.6);
      score += s;
    }

    // Bonus “phrase” si les tokens >1 char apparaissent à la suite
    const seq = multi.map((t) => t.term).join(" ");
    if (seq && refN.includes(seq)) score += 1.2;

    // Proximité + ordre
    if (positions.length > 1) {
      let gaps = 0;
      let inversions = 0;
      for (let i = 1; i < positions.length; i++) {
        const gap = positions[i] - positions[i - 1];
        gaps += Math.max(0, gap);
        if (positions[i] < positions[i - 1]) inversions++;
      }
      score += Math.max(0, 1.5 - Math.min(gaps, 80) / 80);
      if (inversions === 0) score += 0.8;
    }

    // Bonus/pénalité sur la variante si l'utilisateur a saisi une lettre et que FAB est présent
    if (qVariant && hasFab) {
      if (refVar === qVariant) {
        score += 3.0; // bon couplage (ex: "sucree B fab" → "... SUCREE B FAB ...")
      } else if (refVar) {
        score -= 2.0; // mauvaise lettre près de FAB (soft penalty; passe à `return 0` si tu veux être strict)
      }
    }

    return score;
  }
}
